// usePopup — pulls the next motivational popup once per session and on
// significant route changes (so a dose log can trigger an identity popup).
// Suppression decisions are all on the server.

import { useCallback, useEffect, useState } from 'react';
import { apiGet } from '../lib/api';
import type { PopupWire } from '@shared/types';

interface PopupResponse {
  popup: PopupWire | null;
  suppressedReason?: string;
}

export function usePopup(enabled: boolean): {
  popup: PopupWire | null;
  trigger: () => void;
  dismiss: () => void;
} {
  const [popup, setPopup] = useState<PopupWire | null>(null);
  const [tick, setTick] = useState(0);

  const trigger = useCallback(() => setTick((t) => t + 1), []);
  const dismiss = useCallback(() => setPopup(null), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    apiGet<PopupResponse>('/api/popup/next')
      .then((res) => {
        if (cancelled) return;
        setPopup(res?.popup ?? null);
      })
      .catch(() => {
        // Silent — popup endpoint failures are non-blocking
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, tick]);

  return { popup, trigger, dismiss };
}
